/**
 * MessageService - Real-time P2P messaging service
 * Extracted from realP2PDemo.html with complete functionality
 * Handles direct peer messaging, chat history, and message broadcasting
 */

import { P2PMessage } from '../types'
import { DataConnection } from 'peerjs'
import { EventEmitter } from './EventEmitter'

export interface ChatMessage {
  type: 'chat-message'
  from: string
  fromAlias: string
  message: string
  timestamp: number
}

export interface FileTransferMessage {
  type: 'file-transfer'
  from: string
  fromAlias: string
  name: string
  size: number
  data: string // base64 encoded file data
  mimeType: string
  timestamp: number
}

export interface TypingIndicator {
  type: 'typing-indicator'
  from: string
  fromAlias: string
  isTyping: boolean
  timestamp: number
}

export interface FileChunkMessage {
  type: 'file-chunk'
  from: string
  fromAlias: string
  transferId: string
  chunkIndex: number
  totalChunks: number
  chunkData: string
  timestamp: number
}

export interface FileChunkStart {
  type: 'file-chunk-start'
  from: string
  fromAlias: string
  transferId: string
  name: string
  size: number
  mimeType: string
  totalChunks: number
  timestamp: number
}

export interface FileChunkComplete {
  type: 'file-chunk-complete'
  from: string
  fromAlias: string
  transferId: string
  timestamp: number
}

export interface LocationMessage {
  type: 'location'
  from: string
  fromAlias: string
  location: {
    latitude: number
    longitude: number
    accuracy?: number
    altitude?: number
    speed?: number
    heading?: number
    timestamp: number
  }
  shareType: 'one-time' | 'live' | 'duration'
  duration?: number
  address?: string
  timestamp: number
}

export interface LocationRequestMessage {
  type: 'location-request'
  from: string
  fromAlias: string
  requestType: 'one-time' | 'live'
  message?: string
  timestamp: number
}

export interface LocationPermissionMessage {
  type: 'location-permission'
  from: string
  fromAlias: string
  granted: boolean
  shareType?: 'one-time' | 'live' | 'duration'
  timestamp: number
}

export interface MessageHistory {
  peerId: string
  alias: string
  messages: P2PMessage[]
  lastActive: Date
}

export class MessageService extends EventEmitter {
  // Removed unused fields: _localPeerId and _localAlias
  private messageHistory = new Map<string, P2PMessage[]>() // peerId -> messages
  private messageListeners: ((message: P2PMessage, fromPeer: string) => void)[] = []
  private typingListeners: ((peerId: string, isTyping: boolean) => void)[] = []
  private typingTimeouts = new Map<string, NodeJS.Timeout>()

  constructor(_localPeerId: string, _localAlias: string) {
    super()
    // Parameters accepted for interface compatibility but not stored
  }

  private readonly TYPING_TIMEOUT = 3000 // 3 seconds
  private readonly MAX_MESSAGE_LENGTH = 500
  private readonly MAX_HISTORY_PER_PEER = 1000
  // UNIVERSAL FIX: Use same small chunk size for base64 data compatibility across all devices
  private readonly MOBILE_MAX_CHUNK_SIZE = 4096 // 4KB for mobile (base64 safe)
  private readonly DESKTOP_MAX_CHUNK_SIZE = 4096 // 4KB for desktop (base64 safe)
  // private readonly MAX_CHUNK_SIZE = 16384 // Static for initialization, getOptimalChunkSize() used at runtime - Unused
  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
  private messageCount = 0

  // Chunked file transfer state
  private incomingChunks = new Map<string, { chunks: Map<number, string>, totalChunks: number, metadata: any }>()
  private chunkTimeouts = new Map<string, NodeJS.Timeout>()

  /**
   * Send a chat message to a specific peer
   * Extracted from sendChatMessage() function in realP2PDemo.html line 4569
   */
  async sendMessage(
    message: string,
    targetPeerId: string,
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection
  ): Promise<boolean> {
    const cleanMessage = message.trim()
    
    if (!cleanMessage) {
      throw new Error('Message cannot be empty')
    }
    
    if (cleanMessage.length > this.MAX_MESSAGE_LENGTH) {
      throw new Error(`Message too long (max ${this.MAX_MESSAGE_LENGTH} characters)`)
    }
    
    if (!connection.open) {
      throw new Error('Connection not open')
    }

    // Create chat data in same format as realP2PDemo.html
    const chatData: ChatMessage = {
      type: 'chat-message',
      from: fromPeerId,
      fromAlias,
      message: cleanMessage,
      timestamp: Date.now()
    }

    try {
      // Send to peer
      connection.send(chatData)

      // Add to local history as sent message (like addChatMessage('self'))
      const localMessage: P2PMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`,
        content: cleanMessage,
        timestamp: new Date(chatData.timestamp),
        senderId: fromPeerId,
        senderAlias: fromAlias,
        type: 'text',
        recipients: [targetPeerId],
        isSelf: true
      }
      
      this.addToHistory(targetPeerId, localMessage)
      this.messageCount++
      
      // Notify listeners
      this.messageListeners.forEach(listener => {
        listener(localMessage, targetPeerId)
      })
      
      // Emit event for store integration
      this.emit('message-sent', localMessage)
      
      console.log(`💬 Chat sent to ${targetPeerId.slice(0, 8)}...: "${cleanMessage}"`)
      return true
      
    } catch (error) {
      console.error('Failed to send message:', error)
      return false
    }
  }

  /**
   * Send a file to a specific peer with chunking support
   * FIXED: Implements chunking to avoid "Message too big for JSON channel" error
   */
  async sendFile(
    file: File,
    targetPeerId: string,
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection
  ): Promise<boolean> {

    if (!connection.open) {
      throw new Error('Connection not open')
    }

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(`File too large (max ${this.MAX_FILE_SIZE / 1024 / 1024}MB)`)
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      const transferId = `transfer-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`

      reader.onload = async (event) => {
        try {
          const base64Data = event.target?.result as string
          const base64Content = base64Data.split(',')[1] // Remove data URL prefix

          if (!base64Content) {
            throw new Error('Failed to extract base64 content from file')
          }

          // MOBILE FIX: Calculate chunks using adaptive chunk size
          const chunkSize = this.getOptimalChunkSize()
          const totalChunks = Math.ceil(base64Content.length / chunkSize)

          console.log(`📎 Sending file "${file.name}" in ${totalChunks} chunks (${this.formatFileSize(file.size)})`)

          // Send start message
          const startMessage: FileChunkStart = {
            type: 'file-chunk-start',
            from: fromPeerId,
            fromAlias,
            transferId,
            name: file.name,
            size: file.size,
            mimeType: file.type,
            totalChunks,
            timestamp: Date.now()
          }

          connection.send(startMessage)

          // Send chunks with small delay between each
          for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize
            const end = Math.min(start + chunkSize, base64Content.length)
            const chunkData = base64Content.slice(start, end)

            const chunkMessage: FileChunkMessage = {
              type: 'file-chunk',
              from: fromPeerId,
              fromAlias,
              transferId,
              chunkIndex: i,
              totalChunks,
              chunkData,
              timestamp: Date.now()
            }

            connection.send(chunkMessage)

            // Small delay to avoid overwhelming the channel
            if (i < totalChunks - 1) {
              await new Promise(resolve => setTimeout(resolve, 10))
            }
          }

          // Send completion message
          const completeMessage: FileChunkComplete = {
            type: 'file-chunk-complete',
            from: fromPeerId,
            fromAlias,
            transferId,
            timestamp: Date.now()
          }

          connection.send(completeMessage)

          // Add to local history as sent message
          const localMessage: P2PMessage = {
            id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`,
            content: `📎 Sent file: ${file.name} (${this.formatFileSize(file.size)})`,
            timestamp: new Date(),
            senderId: fromPeerId,
            senderAlias: fromAlias,
            type: 'file',
            recipients: [targetPeerId],
            isSelf: true,
            fileData: {
              name: file.name,
              size: file.size,
              mimeType: file.type,
              data: base64Data // Keep full data URL for local use
            }
          }

          this.addToHistory(targetPeerId, localMessage)
          this.messageCount++

          // Notify listeners
          this.messageListeners.forEach(listener => {
            listener(localMessage, targetPeerId)
          })

          // Emit event for store integration
          this.emit('message-sent', localMessage)

          console.log(`📎 File sent successfully to ${targetPeerId.slice(0, 8)}...: "${file.name}" (${totalChunks} chunks)`)
          resolve(true)

        } catch (error) {
          console.error('Failed to send file:', error)
          reject(error)
        }
      }

      reader.onerror = () => {
        reject(new Error('Failed to read file'))
      }

      // Read file as base64 data URL
      reader.readAsDataURL(file)
    })
  }

  /**
   * Handle chunked file transfer start
   */
  handleFileChunkStart(data: FileChunkStart, _fromPeerId: string): void {
    if (data.type !== 'file-chunk-start') {
      return
    }

    console.log(`📎 Starting chunked file receive: "${data.name}" (${data.totalChunks} chunks, ${this.formatFileSize(data.size)})`)

    // Initialize chunk collection
    this.incomingChunks.set(data.transferId, {
      chunks: new Map(),
      totalChunks: data.totalChunks,
      metadata: {
        name: data.name,
        size: data.size,
        mimeType: data.mimeType,
        fromPeerId: _fromPeerId,
        fromAlias: data.fromAlias,
        startTime: Date.now()
      }
    })

    // Set timeout for incomplete transfer (2 minutes)
    const timeout = setTimeout(() => {
      console.warn(`⚠️ File transfer timeout: ${data.transferId}`)
      this.incomingChunks.delete(data.transferId)
      this.chunkTimeouts.delete(data.transferId)
    }, 120000)

    this.chunkTimeouts.set(data.transferId, timeout)
  }

  /**
   * Handle individual file chunk
   */
  handleFileChunk(data: FileChunkMessage, _fromPeerId: string): void {
    if (data.type !== 'file-chunk') {
      return
    }

    const transfer = this.incomingChunks.get(data.transferId)
    if (!transfer) {
      console.warn(`⚠️ Received chunk for unknown transfer: ${data.transferId}`)
      return
    }

    // Store chunk
    transfer.chunks.set(data.chunkIndex, data.chunkData)

    console.log(`📎 Received chunk ${data.chunkIndex + 1}/${data.totalChunks} for "${transfer.metadata.name}"`)

    // Check if all chunks received
    if (transfer.chunks.size === transfer.totalChunks) {
      this.assembleFileFromChunks(data.transferId, transfer)
    }
  }

  /**
   * Handle file transfer completion
   */
  handleFileChunkComplete(data: FileChunkComplete, _fromPeerId: string): void {
    if (data.type !== 'file-chunk-complete') {
      return
    }

    const transfer = this.incomingChunks.get(data.transferId)
    if (!transfer) {
      console.warn(`⚠️ Received completion for unknown transfer: ${data.transferId}`)
      return
    }

    // Double-check assembly in case chunks arrived out of order
    if (transfer.chunks.size === transfer.totalChunks) {
      this.assembleFileFromChunks(data.transferId, transfer)
    } else {
      console.warn(`⚠️ Incomplete file transfer: ${transfer.chunks.size}/${transfer.totalChunks} chunks received`)
    }
  }

  /**
   * Assemble file from chunks
   */
  private assembleFileFromChunks(transferId: string, transfer: any): void {
    try {
      // Sort chunks and reassemble
      const sortedChunks: string[] = []
      for (let i = 0; i < transfer.totalChunks; i++) {
        const chunk = transfer.chunks.get(i)
        if (!chunk) {
          throw new Error(`Missing chunk ${i}`)
        }
        sortedChunks.push(chunk)
      }

      const reassembledBase64 = sortedChunks.join('')
      const dataUrl = `data:${transfer.metadata.mimeType};base64,${reassembledBase64}`

      // Create message
      const senderName = transfer.metadata.fromAlias || 'Unknown User'
      const message: P2PMessage = {
        id: `file-${Date.now()}-${transfer.metadata.fromPeerId.slice(-8)}`,
        content: `📎 Received file: ${transfer.metadata.name} (${this.formatFileSize(transfer.metadata.size)})`,
        timestamp: new Date(),
        senderId: transfer.metadata.fromPeerId,
        senderAlias: senderName,
        type: 'file',
        recipients: [],
        isSelf: false,
        fileData: {
          name: transfer.metadata.name,
          size: transfer.metadata.size,
          mimeType: transfer.metadata.mimeType,
          data: dataUrl
        }
      }

      this.addToHistory(transfer.metadata.fromPeerId, message)
      this.messageCount++

      // Notify listeners
      this.messageListeners.forEach(listener => {
        listener(message, transfer.metadata.fromPeerId)
      })

      // Emit event for store integration
      this.emit('message-received', message)

      // Cleanup
      this.incomingChunks.delete(transferId)
      const timeout = this.chunkTimeouts.get(transferId)
      if (timeout) {
        clearTimeout(timeout)
        this.chunkTimeouts.delete(transferId)
      }

      const duration = Date.now() - transfer.metadata.startTime
      console.log(`✅ File assembled successfully: "${transfer.metadata.name}" (${transfer.totalChunks} chunks, ${duration}ms)`)

    } catch (error) {
      console.error(`❌ Failed to assemble file from chunks:`, error)

      // Cleanup on error
      this.incomingChunks.delete(transferId)
      const timeout = this.chunkTimeouts.get(transferId)
      if (timeout) {
        clearTimeout(timeout)
        this.chunkTimeouts.delete(transferId)
      }
    }
  }

  /**
   * Handle incoming file transfer (legacy support)
   * Based on handleFileTransfer() function in useSelft34.html lines 2582-2592
   */
  handleIncomingFile(data: FileTransferMessage, fromPeerId: string): void {
    if (data.type !== 'file-transfer') {
      return
    }

    // Try to get display name from peer's data, fallback to alias, then generic name
    const senderName = data.fromAlias || 'Unknown User'

    const message: P2PMessage = {
      id: `file-${data.timestamp}-${fromPeerId.slice(-8)}`,
      content: `📎 Received file: ${data.name} (${this.formatFileSize(data.size)})`,
      timestamp: new Date(data.timestamp),
      senderId: fromPeerId,
      senderAlias: senderName,
      type: 'file',
      recipients: [],
      isSelf: false,
      fileData: {
        name: data.name,
        size: data.size,
        mimeType: data.mimeType,
        data: data.data
      }
    }

    this.addToHistory(fromPeerId, message)
    this.messageCount++

    // Notify listeners
    this.messageListeners.forEach(listener => {
      listener(message, fromPeerId)
    })

    // Emit event for store integration
    this.emit('message-received', message)

    console.log(`📎 File from ${senderName}: "${data.name}" (${this.formatFileSize(data.size)})`)
  }

  /**
   * Format file size for display
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * Handle incoming chat message
   * Extracted from handleData() case 'chat-message' in realP2PDemo.html lines 2134-2148
   */
  handleIncomingMessage(data: ChatMessage, fromPeerId: string): void {
    if (data.type !== 'chat-message') {
      return
    }

    // Try to get display name from peer's selfta data, fallback to alias, then generic name
    const senderName = data.fromAlias || 'Unknown User'

    const message: P2PMessage = {
      id: `msg-${data.timestamp}-${fromPeerId.slice(-8)}`,
      content: data.message,
      timestamp: new Date(data.timestamp),
      senderId: fromPeerId,
      senderAlias: senderName,
      type: 'text',
      recipients: [],
      isSelf: false
    }

    this.addToHistory(fromPeerId, message)
    this.messageCount++

    // Notify listeners
    this.messageListeners.forEach(listener => {
      listener(message, fromPeerId)
    })

    // Emit event for store integration
    this.emit('message-received', message)

    console.log(`💬 Chat from ${senderName}: "${data.message}"`)
  }

  /**
   * Get message history for a peer
   */
  getMessageHistory(peerId: string): P2PMessage[] {
    return this.messageHistory.get(peerId) || []
  }

  /**
   * Get all message histories
   */
  getAllMessageHistories(): MessageHistory[] {
    const histories: MessageHistory[] = []
    
    for (const [peerId, messages] of this.messageHistory) {
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1]
        if (lastMessage) {
          histories.push({
            peerId,
            alias: lastMessage.senderAlias,
            messages,
            lastActive: lastMessage.timestamp
          })
        }
      }
    }
    
    // Sort by last activity
    return histories.sort((a, b) => b.lastActive.getTime() - a.lastActive.getTime())
  }

  /**
   * Add message to history (extracted from addChatMessage function)
   */
  private addToHistory(peerId: string, message: P2PMessage): void {
    if (!this.messageHistory.has(peerId)) {
      this.messageHistory.set(peerId, [])
    }
    
    const history = this.messageHistory.get(peerId)!
    history.push(message)
    
    // Limit history size
    if (history.length > this.MAX_HISTORY_PER_PEER) {
      history.splice(0, history.length - this.MAX_HISTORY_PER_PEER)
    }
  }

  /**
   * Clear message history for a peer (extracted from clearChatHistory)
   */
  clearHistory(peerId: string): void {
    this.messageHistory.delete(peerId)
    console.log(`🗑️ Cleared message history for ${peerId.slice(0, 8)}...`)
  }

  /**
   * Clear all message history
   */
  clearAllHistory(): void {
    this.messageHistory.clear()
    console.log('🗑️ Cleared all message history - Fresh start in hollowSelft spirit 🌸')
  }

  /**
   * Add message listener
   */
  onMessage(listener: (message: P2PMessage, fromPeer: string) => void): () => void {
    this.messageListeners.push(listener)
    return () => {
      const index = this.messageListeners.indexOf(listener)
      if (index > -1) {
        this.messageListeners.splice(index, 1)
      }
    }
  }

  /**
   * Send typing indicator
   */
  sendTypingIndicator(
    _targetPeerId: string, 
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection,
    isTyping: boolean = true
  ): void {
    if (!connection.open) return
    
    try {
      const typingData: TypingIndicator = {
        type: 'typing-indicator',
        from: fromPeerId,
        fromAlias,
        isTyping,
        timestamp: Date.now()
      }
      connection.send(typingData)
    } catch (error) {
      console.warn('Failed to send typing indicator:', error)
    }
  }

  /**
   * Handle typing indicator
   */
  handleTypingIndicator(data: TypingIndicator, fromPeerId: string): void {
    if (data.type !== 'typing-indicator') return
    
    // Clear existing timeout
    const existingTimeout = this.typingTimeouts.get(fromPeerId)
    if (existingTimeout) {
      clearTimeout(existingTimeout)
    }
    
    // Notify listeners
    this.typingListeners.forEach(listener => {
      listener(fromPeerId, data.isTyping)
    })
    
    // Emit events for store integration
    if (data.isTyping) {
      this.emit('typing-started', { peerId: fromPeerId })
    } else {
      this.emit('typing-stopped', { peerId: fromPeerId })
    }
    
    // Set timeout to clear typing indicator
    if (data.isTyping) {
      const timeout = setTimeout(() => {
        this.typingListeners.forEach(listener => {
          listener(fromPeerId, false)
        })
        this.emit('typing-stopped', { peerId: fromPeerId })
        this.typingTimeouts.delete(fromPeerId)
      }, this.TYPING_TIMEOUT)
      
      this.typingTimeouts.set(fromPeerId, timeout)
    }
  }

  /**
   * Add typing listener
   */
  onTyping(listener: (peerId: string, isTyping: boolean) => void): () => void {
    this.typingListeners.push(listener)
    return () => {
      const index = this.typingListeners.indexOf(listener)
      if (index > -1) {
        this.typingListeners.splice(index, 1)
      }
    }
  }

  /**
   * Handle P2P data (router method)
   * FIXED: Added support for chunked file transfer messages
   */
  handleP2PData(data: unknown, fromPeerId: string): boolean {
    if (typeof data !== 'object' || !data) {
      return false
    }

    const typedData = data as { type?: string }

    switch (typedData.type) {
      case 'chat-message':
        this.handleIncomingMessage(typedData as ChatMessage, fromPeerId)
        return true

      case 'file-transfer':
        this.handleIncomingFile(typedData as FileTransferMessage, fromPeerId)
        return true

      case 'file-chunk-start':
        this.handleFileChunkStart(typedData as FileChunkStart, fromPeerId)
        return true

      case 'file-chunk':
        this.handleFileChunk(typedData as FileChunkMessage, fromPeerId)
        return true

      case 'file-chunk-complete':
        this.handleFileChunkComplete(typedData as FileChunkComplete, fromPeerId)
        return true

      case 'typing-indicator':
        this.handleTypingIndicator(typedData as TypingIndicator, fromPeerId)
        return true

      case 'location':
        this.handleLocationMessage(typedData as LocationMessage, fromPeerId)
        return true

      case 'location-request':
        this.handleLocationRequest(typedData as LocationRequestMessage, fromPeerId)
        return true

      case 'location-permission':
        this.handleLocationPermission(typedData as LocationPermissionMessage, fromPeerId)
        return true

      default:
        return false // Not handled by this service
    }
  }

  /**
   * Get all messages across all conversations
   */
  getMessages(): P2PMessage[] {
    const allMessages: P2PMessage[] = []
    for (const messages of this.messageHistory.values()) {
      allMessages.push(...messages)
    }
    return allMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }

  /**
   * Get current typing indicators
   */
  getTypingIndicators(): { peerId: string; isTyping: boolean }[] {
    const indicators: { peerId: string; isTyping: boolean }[] = []
    for (const peerId of this.typingTimeouts.keys()) {
      indicators.push({ peerId, isTyping: true })
    }
    return indicators
  }

  /**
   * Check if a specific peer is typing
   */
  isPeerTyping(peerId: string): boolean {
    return this.typingTimeouts.has(peerId)
  }

  /**
   * Clear all messages (alias for clearAllHistory for test compatibility)
   */
  clearMessages(): void {
    this.clearAllHistory()
  }

  /**
   * Get message statistics
   */
  getStats(): {
    totalMessages: number
    broadcastHistory: number
    activeTypers: number
    messageRetentionHours: number
  } {
    let totalMessages = 0
    for (const messages of this.messageHistory.values()) {
      totalMessages += messages.length
    }

    return {
      totalMessages,
      broadcastHistory: this.messageHistory.size,
      activeTypers: this.typingTimeouts.size,
      messageRetentionHours: 24 // Standard retention period
    }
  }

  /**
   * Get recent connections with message activity
   */
  getRecentConnections(limit: number = 10): { peerId: string; alias: string; lastMessage: string; timestamp: Date }[] {
    const connections: { peerId: string; alias: string; lastMessage: string; timestamp: Date }[] = []
    
    for (const [peerId, messages] of this.messageHistory) {
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1]
        if (lastMessage) {
          connections.push({
            peerId,
            alias: lastMessage.senderAlias,
            lastMessage: lastMessage.content,
            timestamp: lastMessage.timestamp
          })
        }
      }
    }
    
    return connections
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit)
  }

  /**
   * Update local peer information
   */
  updateLocalPeer(_peerId: string, _alias: string): void {
    // No-op since local peer fields are no longer stored
  }
  
  /**
   * Handle incoming location message
   */
  handleLocationMessage(data: LocationMessage, fromPeerId: string): void {
    if (data.type !== 'location') {
      return
    }

    const senderName = data.fromAlias || 'Unknown User'
    const message: P2PMessage = {
      id: `location-${data.timestamp}-${fromPeerId.slice(-8)}`,
      content: `📍 Shared location`,
      timestamp: new Date(data.timestamp),
      senderId: fromPeerId,
      senderAlias: senderName,
      type: 'location',
      recipients: [],
      isSelf: false,
      locationData: {
        latitude: data.location.latitude,
        longitude: data.location.longitude,
        accuracy: data.location.accuracy,
        altitude: data.location.altitude,
        speed: data.location.speed,
        heading: data.location.heading,
        timestamp: data.location.timestamp,
        shareType: data.shareType,
        duration: data.duration,
        address: data.address
      }
    }

    this.addToHistory(fromPeerId, message)
    this.messageCount++

    // Notify listeners
    this.messageListeners.forEach(listener => {
      listener(message, fromPeerId)
    })

    // Emit event for store integration
    this.emit('message-received', message)

    console.log(`📍 Location from ${senderName}: ${data.location.latitude}, ${data.location.longitude}`)
  }

  /**
   * Handle location request message
   */
  handleLocationRequest(data: LocationRequestMessage, fromPeerId: string): void {
    if (data.type !== 'location-request') {
      return
    }

    const senderName = data.fromAlias || 'Unknown User'
    const message: P2PMessage = {
      id: `location-request-${data.timestamp}-${fromPeerId.slice(-8)}`,
      content: data.message || `${senderName} is requesting your location`,
      timestamp: new Date(data.timestamp),
      senderId: fromPeerId,
      senderAlias: senderName,
      type: 'location-request',
      recipients: [],
      isSelf: false,
      locationRequest: {
        requestType: data.requestType,
        message: data.message
      }
    }

    this.addToHistory(fromPeerId, message)
    this.messageCount++

    // Notify listeners
    this.messageListeners.forEach(listener => {
      listener(message, fromPeerId)
    })

    // Emit event for store integration
    this.emit('location-request-received', { message, fromPeerId })

    console.log(`🗺️ Location request from ${senderName}`)
  }

  /**
   * Handle location permission response
   */
  handleLocationPermission(data: LocationPermissionMessage, fromPeerId: string): void {
    if (data.type !== 'location-permission') {
      return
    }

    const senderName = data.fromAlias || 'Unknown User'
    const message: P2PMessage = {
      id: `location-permission-${data.timestamp}-${fromPeerId.slice(-8)}`,
      content: data.granted
        ? `${senderName} granted location access`
        : `${senderName} denied location access`,
      timestamp: new Date(data.timestamp),
      senderId: fromPeerId,
      senderAlias: senderName,
      type: 'location-permission',
      recipients: [],
      isSelf: false,
      locationPermission: {
        granted: data.granted,
        shareType: data.shareType
      }
    }

    this.addToHistory(fromPeerId, message)
    this.messageCount++

    // Notify listeners
    this.messageListeners.forEach(listener => {
      listener(message, fromPeerId)
    })

    // Emit event for store integration
    this.emit('location-permission-received', { message, fromPeerId })

    console.log(`🔐 Location permission from ${senderName}: ${data.granted ? 'granted' : 'denied'}`)
  }

  /**
   * Send location message to peer
   */
  async sendLocationMessage(
    location: any,
    shareType: 'one-time' | 'live' | 'duration',
    targetPeerId: string,
    fromPeerId: string,
    fromAlias: string,
    connection: any,
    duration?: number,
    address?: string
  ): Promise<boolean> {
    if (!connection.open) {
      throw new Error('Connection not open')
    }

    const locationData: LocationMessage = {
      type: 'location',
      from: fromPeerId,
      fromAlias,
      location: {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        altitude: location.altitude,
        speed: location.speed,
        heading: location.heading,
        timestamp: location.timestamp
      },
      shareType,
      duration,
      address,
      timestamp: Date.now()
    }

    try {
      connection.send(locationData)

      // Add to local history as sent message
      const localMessage: P2PMessage = {
        id: `location-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`,
        content: '📍 Location shared',
        timestamp: new Date(locationData.timestamp),
        senderId: fromPeerId,
        senderAlias: fromAlias,
        type: 'location',
        recipients: [targetPeerId],
        isSelf: true,
        locationData: {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          altitude: location.altitude,
          speed: location.speed,
          heading: location.heading,
          timestamp: location.timestamp,
          shareType,
          duration,
          address
        }
      }

      this.addToHistory(targetPeerId, localMessage)
      this.emit('message-sent', localMessage)

      console.log(`📍 Location sent to ${targetPeerId}`)
      return true
    } catch (error) {
      console.error('Failed to send location:', error)
      return false
    }
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    // Clear typing timeouts
    for (const timeout of this.typingTimeouts.values()) {
      clearTimeout(timeout)
    }
    this.typingTimeouts.clear()

    // Clear chunk transfer timeouts
    for (const timeout of this.chunkTimeouts.values()) {
      clearTimeout(timeout)
    }
    this.chunkTimeouts.clear()
    this.incomingChunks.clear()

    // Clear listeners
    this.messageListeners.length = 0
    this.typingListeners.length = 0

    console.log('MessageService cleanup completed')
  }
  
  /**
   * Destroy service and clean up all resources
   */
  destroy(): void {
    this.cleanup()
    super.destroy() // Clean up event listeners
    console.log('MessageService destroyed')
  }

  /**
   * Reset service (for testing)
   */
  reset(): void {
    this.cleanup()
    this.messageHistory.clear()
    this.messageCount = 0
    super.destroy() // Clear event listeners
    console.log('MessageService reset completed')
  }

  /**
   * MOBILE FIX: Get optimal chunk size based on device type
   */
  private getOptimalChunkSize(): number {
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    return isMobile ? this.MOBILE_MAX_CHUNK_SIZE : this.DESKTOP_MAX_CHUNK_SIZE
  }

  // Removed unused method: validateMessageSize for TypeScript cleanup
}